import dayjs from "dayjs";
import Link from "next/link";
import "dayjs/locale/tr";
import BookmarkIcon from "@/components/icons/bookmark";
import LikeIcon from "@/components/icons/like";
import MessageIcon from "@/components/icons/message";
import ShareIcon from "@/components/icons/share";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import UserCard from "@/components/ui/user-card";
import PlayButton from "@/components/ui/video/play-button";
import { cn } from "@/lib/utils";
import { ContentType } from "@/types/content";
import type { ContentCard } from "@/types/dashboard";

dayjs.locale("tr");

const SearchHorizontalContentCard = ({ content }: { content: ContentCard }) => {
  const showAuthor =
    content.author &&
    ![ContentType.Video, ContentType.Quiz].includes(content.type);
  return (
    <div
      className={cn(
        "p-0  flex flex-row-reverse md:flex-row gap-2 justify-between",
      )}
    >
      <div
        className={cn("flex flex-col gap-2 w-full", {
          "justify-between": !showAuthor,
          "justify-end": showAuthor,
        })}
      >
        {showAuthor && (
          <div className="hidden md:block">
            <UserCard user={content.author} />
          </div>
        )}
        {content.category && (
          <span className="text-[10px] leading-3.5 font-semibold text-neutral-500">
            {content.category?.name}
          </span>
        )}
        <Link className="hover:underline" href={`/${content.slug}`}>
          <h1 className="text-lg leading-5 md:text-[26px] md:leading-7.5 font-bold line-clamp-2">
            {content.title}
          </h1>
        </Link>
        <p className="text-xs md:text-sm md:leading-5 font-medium w-full line-clamp-2 mb-2">
          {content.lead || content.summary || ""}
        </p>
        <div className="flex flex-row justify-between w-full">
          <div className="mt-1 flex flex-row items-center gap-3">
            <span className="text-xs md:text-sm leading-5 font-semibold text-neutral-400">
              {dayjs(content.publishedAt).format("DD MMMM YYYY")}
            </span>
            <span className="inline-flex items-center justify-center">
              <Link className="group" href={`/${content.slug}`}>
                <LikeIcon
                  className={
                    content.isLiked
                      ? "size-6 stroke-[1.5] stroke-neutral-300 fill-neutral-300"
                      : "size-6 stroke-[1.5] stroke-neutral-400 group-hover:fill-neutral-300"
                  }
                />
              </Link>
              {content.likeCount}
            </span>
            <span className="inline-flex items-center justify-center">
              <Link className="group" href={`/${content.slug}`}>
                <MessageIcon className="stroke-neutral-400 size-6 stroke-[1.5] group-hover:stroke-brand-blue-500" />
              </Link>
              {content.commentCount}
            </span>
          </div>
          <div className="flex flex-row gap-2 justify-center items-center">
            <Link className="group" href={`/${content.slug}`}>
              <BookmarkIcon className="size-6 stroke-[1.5] stroke-neutral-400 fill-none group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100" />
            </Link>
            <Button size="icon-sm" variant="ghost">
              <ShareIcon className="stroke-brand-blue-500 size-6 stroke-[1.5]" />
            </Button>
          </div>
        </div>
      </div>
      <div className="shrink-0 justify-center items-center flex flex-col relative w-36 md:w-77 h-fit md:h-43.5 rounded-2xl overflow-clip">
        <Link className="hover:underline" href={`/${content.slug}`}>
          <ExtendedImage
            alt={`${content.title} image`}
            className="aspect-square md:aspect-video! w-36 md:w-77 object-cover"
            height={174}
            src={content.coverImageUrl || (content.thumbnailUrl as string)}
            width={308}
          />
          {content.type === 1 && <PlayButton />}
        </Link>
      </div>
    </div>
  );
};

export default SearchHorizontalContentCard;
